import HeroBanner from "@/components/HeroBanner.vue";
import * as themeColors from "@assets/styles/colors.module.scss";
import { useBuilderShowIf } from "../composables/builderFieldUtility";
import { builderCta } from "@/helpers/builder-init/index";
const colors = ["None", ...Object.keys(themeColors)];
const { hasValue, hasValueEqualTo, hasRichText, isVideo } = useBuilderShowIf();

export const heroBannerInit = {
    component: HeroBanner,
    name: "Hero Banner",
    canHaveChildren: false,
    inputs: [
        // Banner Media
        {
            name: "banner_media",
            friendlyName: "Banner Media",
            type: "object",
            folded: false,
            defaultValue: {
                alt_text: "Hero Banner",
                bg_color: "None",
                inset_banner: false,
                aria_label: "Hero Banner",
                default_video_muted_desktop: true,
                default_video_muted_mobile: true
            },
            subFields: [
                // desktop media
                {
                    name: "desktop_media",
                    friendlyName: "Media for Desktop",
                    helperText:
                        "Will be used for mobile when no mobile media is given. Allowed file types - .jpeg, .png, .mp4, .gif, .svg",
                    allowedFileTypes: ["jpeg", "png", "mp4", "gif", "svg"],
                    type: "file"
                },
                {
                    name: "ambient_video_desktop",
                    friendlyName: "Ambient Video (Desktop)",
                    helperText:
                        "If checked, the video will autoplay on loop, show no controls, and allow overlay text content. If unchecked, the video will show controls and hide any overlay content",
                    type: "boolean",
                    showIf: isVideo("desktop_media")
                },
                {
                    name: "default_video_muted_desktop",
                    friendlyName: "Mute Video Audio by Default (Desktop)",
                    helperText:
                        "If checked, the video will be muted by default in the video controls",
                    type: "boolean",
                    showIf: isVideo("desktop_media")
                },
                // mobile img
                {
                    name: "mobile_media",
                    friendlyName: "Media for Mobile",
                    helperText:
                        "Allowed file types - .jpeg, .png, .mp4, .gif, .svg",
                    allowedFileTypes: ["jpeg", "png", "mp4", "gif", "svg"],
                    type: "file"
                },
                {
                    name: "ambient_video_mobile",
                    friendlyName: "Ambient Video (Mobile)",
                    helperText:
                        "If checked, the video will autoplay on loop, show no controls, will be muted, and allow overlay text content. If unchecked, the video will show controls and hide any overlay content",
                    type: "boolean",
                    showIf: isVideo("mobile_media")
                },
                {
                    name: "default_video_muted_mobile",
                    friendlyName: "Mute Video Audio by Default (Mobile)",
                    helperText:
                        "If checked, the video will be muted by default in the video controls",
                    type: "boolean",
                    showIf: isVideo("mobile_media")
                },
                // alt text
                {
                    name: "alt_text",
                    friendlyName: "Alt Text",
                    type: "string",
                    showIf: hasValue("desktop_media"),
                    required: true
                },
                // background color
                {
                    name: "bg_color",
                    friendlyName: "Background Color",
                    helperText: "This also serves as the background color for the banner text content when 'Show Text Below Image on Mobile' is set on.",
                    type: "string",
                    enum: colors
                },
                // min height (px)
                {
                    name: "min_height",
                    friendlyName: "Min Banner Height (px)",
                    helperText: "",
                    type: "number"
                },
                // max height (px)
                {
                    name: "max_height",
                    friendlyName: "Max Banner Height (px)",
                    helperText: "",
                    type: "number"
                },
                // inset banner
                {
                    name: "inset_banner",
                    friendlyName: "Inset Banner",
                    type: "boolean"
                },
                // aria label
                {
                    name: "aria_label",
                    friendlyName: "Aria Label",
                    type: "string"
                }
            ]
        },
        // Banner Content
        {
            name: "banner_content",
            friendlyName: "Banner Content",
            type: "object",
            defaultValue: {
                content_align_vertical: "middle",
                content_align_horizontal: "left",
                text_bg_color: "None",
                text_alignment: "left",
                desktop_text_alignment: "left",
                mobile_text_below: false
            },
            folded: false,
            subFields: [
                // content align vertical
                {
                    name: "content_align_vertical",
                    friendlyName: "Vertical Content Alignment",
                    type: "string",
                    enum: ["top", "middle", "bottom"]
                },
                // content align horizontal
                {
                    name: "content_align_horizontal",
                    friendlyName: "Horizontal Content Alignment",
                    type: "string",
                    enum: ["left", "center", "right"]
                },
                // text_bg_color
                {
                    name: "text_bg_color",
                    friendlyName: "Text Background Color",
                    type: "string",
                    enum: colors
                },
                // text align (both mobile and desktop)
                {
                    name: "text_alignment",
                    friendlyName: "Text Alignment",
                    type: "string",
                    enum: ["left", "center", "right"]
                },
                // desktop text align (override for desktop)
                {
                    name: "desktop_text_alignment",
                    friendlyName: "Desktop Text Alignment",
                    type: "string",
                    enum: ["left", "center", "right"]
                },
                // mobile text below
                {
                    name: "mobile_text_below",
                    friendlyName: "Show Text Below Image on Mobile",
                    type: "boolean"
                }
            ]
        },
        // Eyebrow
        {
            name: "eyebrow",
            friendlyName: "Eyebrow",
            type: "object",
            folded: false,
            defaultValue: {
                color: "None"
            },
            subFields: [
                // text
                {
                    name: "text",
                    friendlyName: "Text",
                    type: "string"
                },
                // color
                {
                    name: "color",
                    friendlyName: "Color",
                    type: "string",
                    enum: colors,
                    showIf: hasValue("text")
                }
            ]
        },
        // Heading
        {
            name: "heading",
            friendlyName: "Heading",
            type: "object",
            folded: false,
            defaultValue: {
                color: "None"
            },
            subFields: [
                // text
                {
                    name: "text",
                    friendlyName: "Text",
                    type: "richText"
                },
                // color
                {
                    name: "color",
                    friendlyName: "Color",
                    type: "string",
                    enum: colors,
                    showIf: hasRichText("text")
                },
                // // font_size_mobile
                // {
                //     name: "font_size_mobile",
                //     friendlyName: "Font Size (Mobile)",
                //     type: "number",
                //     showIf: hasRichText("text")
                // },
                // // font_size_desktop
                // {
                //     name: "font_size_desktop",
                //     friendlyName: "Font Size (Desktop)",
                //     type: "number",
                //     showIf: hasRichText("text")
                // }
            ]
        },
        // Description
        {
            name: "description",
            friendlyName: "Description",
            type: "object",
            folded: false,
            defaultValue: {
                color: "None"
            },
            subFields: [
                // text
                {
                    name: "text",
                    friendlyName: "Text",
                    type: "richText"
                },
                // color
                {
                    name: "color",
                    friendlyName: "Color",
                    type: "string",
                    enum: colors,
                    showIf: hasRichText("text")
                }
            ]
        },
        // Badge
        {
            name: "badge",
            type: "object",
            defaultValue: {
                width_mobile: "60",
                width_desktop: "100",
                alt_text: "Badge"
            },
            folded: false,
            subFields: [
                {
                    name: "image",
                    type: "file",
                    helperText: "Allowed file types - .jpeg, .png, .gif, .svg",
                    allowedFileTypes: ["jpeg", "png", "gif", "svg"]
                },
                {
                    name: "width_mobile",
                    friendlyName: "Mobile Width (px)",
                    type: "number",
                    showIf: hasValue("image")
                },
                {
                    name: "width_desktop",
                    friendlyName: "Desktop Width (px)",
                    type: "number",
                    showIf: hasValue("image")
                },
                {
                    name: "alt_text",
                    friendlyName: "Alt Text",
                    type: "string",
                    showIf: hasValue("image")
                }
            ]
        },
        // CTAs
        {
            name: "ctas",
            friendlyName: "CTAs",
            helperText: "Limit 4 buttons/links",
            type: "list",
            defaultValue: [],
            folded: false,
            subFields: [...builderCta.subFields],
            onChange: (options) => {
                if (options.get("ctas").length > 4) {
                    options.set("ctas", options.get("ctas").slice(0, 2));
                    alert("Cannot exceed 2 CTAs.");
                }
            }
        },
        // Feature Bar
        {
            name: "feature_bar",
            type: "object",
            defaultValue: {
                background_color: "None",
                text_color: "None"
            },
            folded: false,
            subFields: [
                // feature icons
                {
                    name: "feature_icons",
                    helperText: "The number of feature icons must be one or even. Anything outside of these bounds will not show.",
                    type: "list",
                    folded: false,
                    defaultValue: [],
                    subFields: [
                        {
                            name: "image",
                            type: "file",
                            allowedFileTypes: ["jpeg", "png", "gif", "svg"],
                            required: true
                        },
                        {
                            name: "text",
                            type: "string"
                        },
                        {
                            name: "aria_label",
                            type: "string"
                        },
                        {
                            name: "alt_text",
                            type: "string"
                        }
                    ]
                },
                // feature bar color
                {
                    name: "bg_color",
                    friendlyName: "Background Color",
                    helperText: "",
                    type: "string",
                    enum: colors
                },
                // feature bar text color
                {
                    name: "text_color",
                    friendlyName: "Text Color",
                    helperText: "",
                    type: "string",
                    enum: colors
                }
            ]
        }
    ]
};
